import { Component, OnInit, Input, ViewChild, Output, EventEmitter, SimpleChange } from '@angular/core';
import { Order, OrdersService } from '../../doctors/orders/orders.service';
import { EupRoutesService } from '@core/eupRoutes.service';
import { StorageType, FeatureToggle, SoftwareOptionsForCompany } from '../enums';
import { DownloadLinkModal } from '@shared/downloadLinkModal/downloadLinkModal.component';
import { TranslateService } from '@ngx-translate/core';
import { GlobalSettingsService } from '@core/globalSettings.service';
import { IteroLabService } from '@shared/iteroLab.service';
import { FeaturesToggleSettingsService } from 'app/featuresToggleSettings/service/featuresToggleSettings.service';
import { SoftwareOptionsService } from 'app/core/companySoftwareOptions/service/softwareOptions.service';
import { DoctorPairingService } from 'app/services/doctor-pairing/doctor-pairing.service';
import { WindowsApplications } from './WindowsApplications';
import { timer } from 'rxjs';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BaseDestroyable } from '@core/base-destroyable';
import { ShellContextService } from '../../services/shell-context/shell-context.service';
import { TimberService } from 'app/services/logging/timber.service';

@Component({
	selector: 'windows-app-button',
	templateUrl: './windowsAppButton.component.html',
	styleUrls: ['./windowsAppButton.component.scss'],
	providers: [IteroLabService],
})
export class WindowsAppButtonComponent extends BaseDestroyable implements OnInit {
	// Mandatory
	@Input() public application: string;
	@Input() public order: Order;
	@Input() id: string;

	// Non-mandatory
	@Input() public title: string;
	@Input() public user = 'doctor';
	@Input() public windowsOnly = true;
	@Input() public modalAllowed = true;

	@ViewChild(DownloadLinkModal) downloadLinkModal: DownloadLinkModal;

	@Output() openModal: EventEmitter<any> = new EventEmitter();

	public orthoCadSetupDownloadUrl: string;
	public iTeroSetupDownloadUrl: string;
	public iOSimDownloadUrl: string;
	public orthoCadSetupDownloadUrl_acs: string;
	public iTeroSetupDownloadUrl_acs: string;
	public iOSimDownloadUrl_acs: string;

	disclaimer = 'Orders.Disclaimer';
	isInvisalignOutcomeSimulatorEnabled = false;
	isProgressAssesmentEnabled = false;
	isCaseAssesmentEnabled = false;
	iosimAuthenticationSuccessSubscription: any;
	isOrthoCadHidden = false;
	modalFooter = false;
	isOrthoModal = false;

	constructor(
		private eupRoutesService: EupRoutesService,
		private orderService: OrdersService,
		private translateService: TranslateService,
		public globalSettingsService: GlobalSettingsService,
		private shellContextService: ShellContextService,
		private iteroLabService: IteroLabService,
		private featureToggleSettingsService: FeaturesToggleSettingsService,
		private softwareOptionsService: SoftwareOptionsService,
		private iosimService: DoctorPairingService,
		private readonly timberService: TimberService
	) {
		super();
	}

	ngOnChanges(changes: SimpleChange): void {
		if (changes['application'].currentValue !== changes['application'].previousValue) {
			this.disclaimer = this.nameMap[changes['application'].currentValue] === 'Orders.OpenWithOrthoCAD' ? 'Orders.Disclaimer' : '';
		}
	}

	ngOnInit(): void {
		this.orthoCadSetupDownloadUrl = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl;
		this.iTeroSetupDownloadUrl = this.eupRoutesService.downloads.iTeroSetupDownloadUrl;
		this.iOSimDownloadUrl = this.eupRoutesService.downloads.iOSimDownloadUrl;
		this.orthoCadSetupDownloadUrl_acs = this.eupRoutesService.downloads.orthoCadSetupDownloadUrl_acs;
		this.iTeroSetupDownloadUrl_acs = this.eupRoutesService.downloads.iTeroSetupDownloadUrl_acs;
		this.iOSimDownloadUrl_acs = this.eupRoutesService.downloads.iOSimDownloadUrl_acs;

		this.shellContextService.selectedCompanyId$.pipe(
			takeUntil(this.componentAlive$),
			switchMap(companyId => this.softwareOptionsService.getCompanySoftwareOptionsKeyValuePair(companyId)),
			map(softwareOptions => Object.values(softwareOptions))
		).subscribe((softwareOptions) => {
			this.isInvisalignOutcomeSimulatorEnabled = softwareOptions.includes(SoftwareOptionsForCompany.InvisalignOutcomeSimulator);
			this.isProgressAssesmentEnabled = softwareOptions.includes(SoftwareOptionsForCompany.ProgressAssesment);
			this.isCaseAssesmentEnabled = softwareOptions.includes(SoftwareOptionsForCompany.CaseAssesment);
			this.isOrthoCadHidden = softwareOptions.includes(SoftwareOptionsForCompany.cswo_Hide_OrthoCAD_Show_3DM);
		});

		if (!this.title) {
			this.title = this.translateService.instant(this.nameMap[this.application]);
		}
		if (this.application === WindowsApplications.Orthocad) {
			this.isOrthoModal = true;
			this.modalFooter = false;
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}

	public get available(): boolean {
		return this.visibilityMap[this.user][this.application];
	}

	public onClick(): void {
		this.orderService
		.getFileStorage(this.order.id)
		.pipe(
			take(1),
			tap((storage: StorageType) => this.onclickHandler(storage))
		)
		.subscribe();
	}

	private onclickHandler(storage: StorageType) {
		const orderId = this.order.id;
		const companyId = this.globalSettingsService.get().selectedCompanyId;
		const appProtocolLink = this.linkMap[this.application];
		const downloadLink = this.installationLinksMap[this.application][storage];
		const name = this.nameMap[this.application];
		const title = this.translateService.instant(name);

		this.timberService.info(`[IOSim WindowsAppButton.onclickHandler] application=${this.application}, orderId=${orderId}, appProtocolLink=${appProtocolLink}`, { module: 'WindowsAppButtonComponent' });

		if (this.application?.includes('iosim')) {
			this.openPairedApplication(companyId, orderId, title, downloadLink, appProtocolLink);
		} else if (this.iteroLabService.isNeedToSendToLab(this.order)) {
			this.openLabApplication(title, downloadLink);
		} else {
			this.openApp(title, downloadLink, appProtocolLink);
		}
	}

	private openPairedApplication(companyId, orderId, title, downloadLink, appProtocolLink) {
		const hasToken = this.hasRefreshToken(appProtocolLink);
		const canPair = this.canPairWithIds();

		this.iosimService.isDoctorPaired().pipe(take(1)).subscribe((isPaired) => {
			this.timberService.info(`[IOSim WindowsAppButton.openPairedApplication] hasRefreshToken=${hasToken}, canPairWithIds=${canPair}, isDoctorPaired=${isPaired}, orderId=${orderId}, companyId=${companyId}`, { module: 'WindowsAppButtonComponent' });

			if (canPair && (!hasToken || !isPaired)) {		
				this.timberService.info(`[IOSim WindowsAppButton.openPairedApplication] No refresh token and pairing is enabled — opening IDS login window.`, { module: 'WindowsAppButtonComponent' });
				this.openIdsLoginWindow(companyId, orderId, title, downloadLink, appProtocolLink);
				return;
			}

			this.timberService.info(`[IOSim WindowsAppButton.openPairedApplication] Refresh token present or pairing not enabled — opening app directly.`, { module: 'WindowsAppButtonComponent' });
			this.openApp(title, downloadLink, appProtocolLink);
		});
	}

	private openIdsLoginWindow(companyId, orderId, title, downloadLink, appProtocolLink) {
		this.iosimService.openIdsLoginWindow();
		this.iosimAuthenticationSuccessSubscription = this.iosimService.iosimAuthenticationSuccess.pipe(take(1)).subscribe((isSuccess) => {
			if (!isSuccess) {
				this.timberService.info(`[IOSim WindowsAppButton.openIdsLoginWindow] IDS authentication failed or was cancelled.`, { module: 'WindowsAppButtonComponent' });
				return;
			}

			this.iosimService.clearOAuthTokenCache(companyId);
			this.fetchFreshOrderThenLaunch(companyId, orderId, title, downloadLink);
		});
	}

	private fetchFreshOrderThenLaunch(companyId: number, orderId: number, title: string, downloadLink: string): void {
		const getOrder = () => this.orderService.getDoctorOrder({
			orderId: this.order.id,
			doctorId: this.globalSettingsService.get().selectedDoctorId,
			companyId: this.globalSettingsService.get().selectedCompanyId,
		}).pipe(take(1));

		timer(500).pipe(switchMap(() => getOrder())).subscribe((freshOrder: Order) => {
			this.order = freshOrder;
			const link = this.linkMap[this.application];

			if (this.hasRefreshToken(link)) {
				this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] Attempt 1 — refresh_token found, launching.`, { module: 'WindowsAppButtonComponent' });
				this.onClick();
				return;
			}

			this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] Attempt 1 — no refresh_token yet, retrying after 2 s.`, { module: 'WindowsAppButtonComponent' });

			timer(2000).pipe(switchMap(() => getOrder())).subscribe((retryOrder: Order) => {
				this.order = retryOrder;
				const retryLink = this.linkMap[this.application];

				if (this.hasRefreshToken(retryLink)) {
					this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] Attempt 2 — refresh_token found, launching.`, { module: 'WindowsAppButtonComponent' });
					this.onClick();
					return;
				}

				this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] Attempt 2 — still no refresh_token in getDoctorOrder. Falling back to getIOSimLinkWithOAuth.`, { module: 'WindowsAppButtonComponent' });

				this.iosimService.getIOSimLinkWithOAuth(companyId, orderId)
					.pipe(take(1))
					.subscribe({
						next: (freshLink: string) => {
							if (!this.hasRefreshToken(freshLink)) {
								this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] WARNING — getIOSimLinkWithOAuth returned a link without refresh_token. link=${freshLink}`, { module: 'WindowsAppButtonComponent' });
							} else {
								this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] getIOSimLinkWithOAuth returned token-bearing link — launching.`, { module: 'WindowsAppButtonComponent' });
							}
							this.openApp(title, downloadLink, freshLink);
						},
						error: (err) => {
							this.timberService.info(`[IOSim WindowsAppButton.fetchFreshOrderThenLaunch] ERROR — getIOSimLinkWithOAuth failed: ${err?.message}`, { module: 'WindowsAppButtonComponent' });
						}
					});
			});
		});
	}

	private hasRefreshToken(appProtocolLink: string): boolean {
		const result = appProtocolLink.indexOf('refresh_token=') >= 0;
		this.timberService.info(`[IOSim WindowsAppButton.hasRefreshToken] result=${result}, full appProtocolLink=${appProtocolLink}`, { module: 'WindowsAppButtonComponent' });
		return result;
	}

	private openLabApplication(title, downloadLink) {
		this.iteroLabService
			.prepareOpenApp(this.order.id, this.order.lastDetailsId)
			.pipe(
				take(1),
				tap((url) => {
					this.openApp(title, downloadLink, url);
					this.orderService.updateLabOrderById(
						this.order.id.toString(),
						this.globalSettingsService.get().selectedDoctorId.toString(),
						this.globalSettingsService.get().selectedCompanyId.toString(),
						(order: Order) => {
							// optimistic UI issue
							order.canReturnToDoctor = false;
						}
					);
				})
			)
			.subscribe();
	}

	private openApp(title: string, downloadLink: string, appProtocolLink: string): void {
		this.timberService.info(`[IOSim WindowsAppButton.openApp] Launching protocol: ${appProtocolLink}`, { module: 'WindowsAppButtonComponent' });
		this.orderService.startProtocol(appProtocolLink, true);
		if (this.modalAllowed) {
			this.timberService.info(`[IOSim WindowsAppButton.openApp] Showing download link modal: title=${title}, downloadLink=${downloadLink}`, { module: 'WindowsAppButtonComponent' });
			this.downloadLinkModal.show(title, downloadLink);
		} else {
			this.timberService.info(`[IOSim WindowsAppButton.openApp] Emitting openModal event: title=${title}, downloadLink=${downloadLink}`, { module: 'WindowsAppButtonComponent' });
			this.openModal.emit({ title, downloadLink });
		}
	}

	private canPairWithIds(): boolean {
		const isFeatureAvailable = this.featureToggleSettingsService.isVisibleFeature(FeatureToggle.IdsPairingForIOSIM);
		const softwareOptions = this.globalSettingsService.get().companySoftwareOptions;
		const hasSoftwareOption = this.softwareOptionsService.isSoftwareOptionExists(
			SoftwareOptionsForCompany.shouldEnableDoctorPairing,
			softwareOptions
		);
		const result = isFeatureAvailable && hasSoftwareOption;
		this.timberService.info(`[IOSim WindowsAppButton.canPairWithIds] result=${result} (featureFlag=${isFeatureAvailable}, softwareOption=${hasSoftwareOption})`, { module: 'WindowsAppButtonComponent' });
		return result;
	}

	public get customWidth() {
		return this.widthMap[this.application];
	}

	private get visibilityMap() {
		return {
			doctor: {
				[WindowsApplications.Iosim]: this.order.outcomeSimulatorEnabled && this.isInvisalignOutcomeSimulatorEnabled,
				[WindowsApplications.IosimPa]: this.order.progressAssessmentEnabled && this.isProgressAssesmentEnabled,
				[WindowsApplications.IosimCa]: this.order.iGoCaseAssessmentEnabled && this.isCaseAssesmentEnabled,
				[WindowsApplications.IosimGo]: this.order.iGoOutcomeSimulatorEnabled && this.isInvisalignOutcomeSimulatorEnabled,
				[WindowsApplications.Orthocad]: this.order.canOpenFile && this.order.isOrthoOrder && !this.isOrthoCadHidden
			},
			lab: {
				[WindowsApplications.Orthocad]: this.order.canOpenWithOrthoCad && !this.isOrthoCadHidden
			}
		};
	}

	private get linkMap() {
		return {
			[WindowsApplications.Iosim]: this.order.outcomeSimulatorLink,
			[WindowsApplications.IosimPa]: this.order.progressAssessmentLink,
			[WindowsApplications.IosimCa]: this.order.iGoCaseAssessmentLink,
			[WindowsApplications.IosimGo]: this.order.iGoOutcomeSimulatorLink,
			[WindowsApplications.Orthocad]: this.order.openFileLink,
		};
	}

	private get nameMap() {
		return {
			[WindowsApplications.Iosim]: 'Orders.InvisalignOutcomeSimulator',
			[WindowsApplications.IosimPa]: 'Orders.InvisalignProgressAssessment',
			[WindowsApplications.IosimCa]: 'Orders.InvisalignGoCaseAssessment',
			[WindowsApplications.IosimGo]: 'Orders.InvisalignGoOutcomeSimulator',
			[WindowsApplications.Orthocad]: 'Orders.OpenWithOrthoCAD',
		};
	}

	private get installationLinksMap() {
		const iosimLinks = {
			[StorageType.ACS]: this.iOSimDownloadUrl_acs,
			[StorageType.OriginalFMS]: this.iOSimDownloadUrl,
		};

		const iTeroLinks = {
			[StorageType.ACS]: this.iTeroSetupDownloadUrl_acs,
			[StorageType.OriginalFMS]: this.iTeroSetupDownloadUrl,
		};

		return {
			[WindowsApplications.Iosim]: iosimLinks,
			[WindowsApplications.IosimPa]: iosimLinks,
			[WindowsApplications.IosimCa]: iosimLinks,
			[WindowsApplications.IosimGo]: iosimLinks,
			[WindowsApplications.Orthocad]: {
				[StorageType.ACS]: this.orthoCadSetupDownloadUrl_acs,
				[StorageType.OriginalFMS]: this.orthoCadSetupDownloadUrl,
			},
		};
	}

	private get widthMap() {
		return {
			[WindowsApplications.Iosim]: 'extended',
			[WindowsApplications.IosimPa]: 'extended',
			[WindowsApplications.IosimCa]: 'extended',
			[WindowsApplications.IosimGo]: 'extended',
			[WindowsApplications.Orthocad]: undefined,
		};
	}
}
